import axios from 'axios';
import qs from 'query-string';

/**
 * 报备查询参数接口
 * 用于报备列表的筛选和分页查询
 */
export interface ReportParams {
  /** 报备状态：0全部,1待处理,2重单,3重单可深度,4不重单 */
  state: number;
  /** 客户手机号（可选筛选） */
  mobile?: string;
  /** 当前页码，必填 */
  page: number;
  /** 每页数量，可选 */
  pageSize?: number;
  /** 医院名称（可选筛选） */
  hospital?: string;
}

/**
 * 关联信息接口
 * 描述报备相关的关联数据
 */
export interface RelatedInfo {
  /** 关联手机号 */
  mobile: string;
  /** 关联姓名 */
  name: string;
}

/**
 * 备注信息接口
 * 描述报备的备注内容
 */
export interface NoteInfo {
  /** 备注内容 */
  content: string;
}

/**
 * 消息信息接口
 * 描述报备相关的消息数据
 */
export interface MessageInfo {
  /** 报备ID */
  bid: number;
  /** 医院ID */
  cid: number;
  /** 消息内容 */
  content: string;
  /** 创建时间戳 */
  createtime: number;
  /** 消息ID */
  id: number;
  /** 消息图片 */
  images: string;
  /** 消息状态 */
  /** 消息发送时间 */
  /** 消息状态 */
  status: number;
  times: string;
  /** 消息类型 */
  type: number;
  /** 更新时间戳 */
  updatetime: number;
  /** 消息发送者用户名 */
  username: string;
}

/**
 * 报备记录接口定义
 * 用于描述客户报备的完整信息
 */
export interface ReportRecord {
  /** 报备记录ID */
  id: number;
  /** 报备人ID */
  uuid: number;
  /** 用户ID */
  uid: number;
  /** 团队长ID */
  usuid?: number;
  /** 医院ID */
  cid: number | number[];
  /** 医院名称 */
  hospital_name: string;
  /** 项目 */
  project: string;
  /** 城市 */
  city: string;
  /** 客户姓名 */
  name: string;
  /** 备注图片（JSON字符串） */
  images: string;
  /** 备注图片数组 */
  imageArray: string[];
  /** 报备状态：1-待处理，2-重单，3-重单可深度，4-不重单 */
  state: number;
  /** 是否发送短信：0-否，1-是 */
  issms: number;
  /** 是否隐私报备：0-否，1-是 */
  is_privacy: number;
  /** 显示状态：0-隐藏，1-显示 */
  show_status: number;
  /** 报备时间 */
  times: string;
  /** 创建时间戳 */
  createtime: number;
  /** 更新时间戳 */
  updatetime: number;
  /** 客户手机号 */
  mobile: string;
  /** 客户昵称 */
  nickname: string;
  /** 分组ID */
  group_id: number;
  /** 用户手机号 */
  usmobile: string;
  /** 用户名 */
  username: string;
  /** 上级用户昵称 */
  uusnickname: string;
  /** 上级用户名 */
  uususername: string;
  /** 上级用户手机号 */
  uusmobile: string;
  /** 项目名称 */
  pname: string;
  /** 关联信息 */
  related: RelatedInfo;
  /** 备注信息 */
  note: NoteInfo;
  /** 消息信息 */
  msg_id: number;
  /** 消息发送者用户名 */
  msg_username: string;
  /** 消息内容 */
  msg_content: string;
  /** 消息发送时间 */
  msg_times: string;
  /** 消息更新时间 */
  msg_update: string;
  /** 沟通记录 */
  msg_list: MessageInfo[];
  /** 消息图片 */
  msg_images: string;
}

/**
 * 报备列表响应数据接口
 */
export interface ReportListRes {
  /** 报备记录列表 */
  list: ReportRecord[];
  /** 总记录数 */
  total: number;
}

export interface AddReportParams {
  cid: number[] | string[] | string;
  project: string;
  city: string;
  name: string;
  mobile: string;
  images: string;
  msg: string;
  is_privacy: number;
  uid: number;
  uuid: number;
  is_flow?: number;
  // username: string;
}

/**
 * 医院列表查询参数接口
 */
export interface HospitalParams {
  /** 城市 */
  city: string;
  /** 医院名称关键词 */
  keyword?: string;
}

/**
 * 医院列表数据接口
 */
export interface HospitalRecord {
  /** 医院ID */
  id: number;
  /** 医院所在城市 */
  city: string;
  /** 医院昵称 */
  nickname: string;
  /** 医院LogoURL */
  avatar: string;
  /** 医院简介 */
  brief: string;
}

/**
 * 医院列表响应数据接口
 */
export interface HospitalListRes {
  /** 接口返回状态码 */
  code: number;
  /** 医院列表 */
  list: HospitalRecord[];
  /** 接口返回信息 */
  msg: string;
  /** 接口响应时间戳 */
  time: string;
}

/**
 * 已开通城市列表数据接口
 */
export type CityListRes = string[];

/**
 * 报备项目子项接口
 */
export interface ReportProjectChild {
  /** 子项ID */
  id: number;
  /** 父级ID */
  pid: number;
  /** 子项名称 */
  name: string;
}

/**
 * 报备项目接口
 */
export interface ReportProject {
  /** 项目ID */
  id: number;
  /** 父级ID（顶级为0） */
  pid: number;
  /** 项目名称 */
  name: string;
  /** 子项目列表 */
  child_list: ReportProjectChild[];
}

/**
 * 报备项目列表响应数据接口
 */
export type ReportProjectsRes = ReportProject[];

/**
 * 报备医院记录接口
 */
export interface ReportHospitalRecord {
  /** 医院ID */
  cid: number;
  /** 医院名称 */
  nickname: string;
  /** 所在城市 */
  city: string;
  /** 医院头像 */
  avatar: string;
  /** 医院横幅 */
  banner: string;
  /** 医院等级 */
  level: number;
  /** 报备项目IDs（逗号分隔） */
  report_projects: string;
  /** 是否符合条件 */
  is_comply: boolean;
}

/**
 * 报备医院列表响应数据接口
 */
export interface ReportHospitalsRes {
  /** 一级医院列表（符合条件） */
  level_1: ReportHospitalRecord[];
  /** 二级医院列表（可能不符合条件） */
  level_2: ReportHospitalRecord[];
}

/**
 * 报备医院查询参数接口
 */
export interface ReportHospitalsParams {
  /** 选中的项目ID列表 */
  ids: number[];
  /** 选择的城市 */
  city: string;
}

/**
 * 查询报备列表
 * @param params 查询参数，包含筛选条件和分页信息
 * @returns Promise<ReportListRes> 返回报备列表数据
 */
export function queryReportList(params: ReportParams) {
  return axios.get<ReportListRes>('/business/report_list', {
    params,
    paramsSerializer: (obj) => qs.stringify(obj),
  });
}

/**
 * 创建报备记录
 * @param data 报备记录数据
 * @returns Promise<ReportRecord> 返回创建的报备记录
 */
export function createReport(data: Partial<ReportRecord>) {
  return axios.post<ReportRecord>('/business/report', data);
}

/**
 * 更新报备记录
 * @param id 报备记录ID
 * @param data 更新的数据
 * @returns Promise<ReportRecord> 返回更新后的报备记录
 */
export function updateReport(id: number, data: Partial<ReportRecord>) {
  return axios.put<ReportRecord>(`/business/report/${id}`, data);
}

/**
 * 删除报备记录
 * @param id 报备记录ID
 * @returns Promise<void>
 */
export function deleteReport(id: number) {
  return axios.delete(`/business/report/${id}`);
}

/**
 * 查询医院列表
 * @param params 查询参数，包含城市和关键词
 * @returns Promise<HospitalListRes> 返回医院列表数据
 */
export function queryHospitalList(params: HospitalParams) {
  return axios.get<HospitalListRes>('/tool/company_list', {
    params,
    paramsSerializer: (obj) => qs.stringify(obj),
  });
}

/**
 * 查询已开通城市列表
 * @returns Promise<CityListRes> 返回已开通城市列表数据
 */
export function queryCityList() {
  return axios.get<CityListRes>('/tool/city_list');
}

/**
 * 添加报备记录
 * @param data 报备记录数据
 * @returns Promise<ReportRecord> 返回创建的报备记录
 */
export function addReport(data: AddReportParams) {
  return axios.post<ReportRecord>('/business/addBaobei', data);
}

/**
 * 查询报备项目列表
 * @returns Promise<ReportProjectsRes> 返回项目列表数据（包含两级结构）
 */
export function queryReportProjects() {
  return axios.get<ReportProjectsRes>('/tool/reportProjects');
}

/**
 * 查询报备医院列表
 * @param params 查询参数，包含选中的项目IDs和城市
 * @returns Promise<ReportHospitalsRes> 返回医院列表数据
 */
export function queryReportHospitals(params: ReportHospitalsParams) {
  return axios.post<ReportHospitalsRes>('/tool/reportHospitals', params);
}
